import React, { useState, useEffect } from 'react';
import { Archive, ShieldOff, Shield, TrendingUp, ArrowDownLeft, ArrowUpRight, Clock, Server, Activity, FileText, Sun, Moon, Terminal, Search, RefreshCw, Globe } from 'lucide-react';
import { API_BASE, apiFetch } from '../config';
import { useTheme } from '../ThemeContext';
import { useLanguage } from '../i18n';

const AdminPanel = ({ stats }) => {
  const [tab, setTab] = useState('stats');
  const [sentFiles, setSentFiles] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logSearch, setLogSearch] = useState('');
  const [logLevel, setLogLevel] = useState('');
  const [logFile, setLogFile] = useState('app');
  const [logsLoading, setLogsLoading] = useState(false);
  const { isDark, toggleTheme } = useTheme();
  const { lang, t, switchLang, SUPPORTED_LANGS } = useLanguage();

  const langLabels = { uz: "O'zbek", ru: "Русский", en: "English" };

  const fetchSentFiles = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/api/sent-files`);
      if (res.ok) setSentFiles(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      let url = `${API_BASE}/api/logs?lines=150&file=${logFile}`;
      if (logLevel) url += '&level=' + logLevel;
      if (logSearch.trim()) url += '&search=' + encodeURIComponent(logSearch.trim());
      const res = await apiFetch(url);
      if (res.ok) {
        const data = await res.json();
        setLogs(data.lines || []);
      }
    } catch (err) { console.error(err); }
    setLogsLoading(false);
  };

  const fetchBlockedUsers = async () => {
    setLoading(true);
    try {
      const [blockedRes, usersRes] = await Promise.all([
        apiFetch(`${API_BASE}/api/blocked-users`),
        apiFetch(`${API_BASE}/api/users`)
      ]);
      if (blockedRes.ok) setBlockedUsers(await blockedRes.json());
      if (usersRes.ok) setAllUsers(await usersRes.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => {
    if (tab === 'archive' || tab === 'stats') fetchSentFiles();
    if (tab === 'block') fetchBlockedUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    if (tab === 'logs') fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, logLevel, logFile]);

  const handleBlock = async (chatId) => {
    try {
      const res = await apiFetch(`${API_BASE}/api/block-user?chat_id=${chatId}`, { method: 'POST' });
      if (res.ok) fetchBlockedUsers();
    } catch (err) { console.error(err); }
  };

  const handleUnblock = async (chatId) => {
    try {
      const res = await apiFetch(`${API_BASE}/api/block-user?chat_id=${chatId}`, { method: 'DELETE' });
      if (res.ok) fetchBlockedUsers();
    } catch (err) { console.error(err); }
  };

  const total = (stats.received || 0) + (stats.sent || 0);

  // Haftalik statistika (oxirgi 7 kun)
  const getWeeklyData = () => {
    const days = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().slice(0, 10);
      const dayName = d.toLocaleDateString('uz-UZ', { weekday: 'short' }).slice(0, 2);
      days.push({ date: dateStr, label: dayName, sent: 0 });
    }
    // sentFiles dan kunlik count hisoblash
    sentFiles.forEach(f => {
      if (f.sent_at) {
        const fDate = f.sent_at.slice(0, 10);
        const found = days.find(d => d.date === fDate);
        if (found) found.sent += (f.file_count || 1);
      }
    });
    return days;
  };

  const tabButtons = [
    { id: 'stats', label: t('admin_tab_stats'), icon: TrendingUp },
    { id: 'archive', label: t('admin_tab_archive'), icon: Archive },
    { id: 'block', label: t('admin_tab_block'), icon: Shield },
    { id: 'logs', label: t('admin_tab_logs'), icon: Terminal },
  ];

  return (
    <div className="screen-enter pb-24 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white/90">{t('admin_title')}</h2>
        <div className="flex items-center gap-2">
          {/* Til tanlash */}
          <select
            value={lang}
            onChange={(e) => switchLang(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-[10px] text-white/70 outline-none"
          >
            {SUPPORTED_LANGS.map(l => (
              <option key={l} value={l}>{langLabels[l]}</option>
            ))}
          </select>
          <button
            onClick={toggleTheme}
            className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors active:scale-95"
          >
            {isDark ? <Sun size={16} className="text-amber-400" /> : <Moon size={16} className="text-blue-400" />}
          </button>
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-2">
        {tabButtons.map(tb => (
          <button
            key={tb.id}
            onClick={() => setTab(tb.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold transition-all
              ${tab === tb.id ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'glass-card text-white/50 hover:text-white/70'}`}
          >
            <tb.icon size={14} />
            {tb.label}
          </button>
        ))}
      </div>

      {/* Stats Tab */}
      {tab === 'stats' && (
        <div className="space-y-4 animate-fade-in-up">
          <div className="grid grid-cols-2 gap-3">
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <ArrowDownLeft size={14} className="text-blue-400" />
                </div>
                <span className="text-[10px] text-white/40 uppercase">Qabul</span>
              </div>
              <p className="text-2xl font-bold text-white/90 font-mono">{stats.received || 0}</p>
            </div>
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                  <ArrowUpRight size={14} className="text-cyan-400" />
                </div>
                <span className="text-[10px] text-white/40 uppercase">Yuborildi</span>
              </div>
              <p className="text-2xl font-bold text-white/90 font-mono">{stats.sent || 0}</p>
            </div>
          </div>

          <div className="glass-card p-4 space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <Activity size={14} className="text-white/40" />
              <span className="text-xs text-white/40 uppercase font-semibold">Tizim</span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Server size={14} className="text-emerald-400" />
                <span className="text-sm text-white/60">Server</span>
              </div>
              <span className="text-xs text-emerald-400 font-mono">{stats.status || 'Online'}</span>
            </div>
            <div className="h-px bg-white/5" />
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Clock size={14} className="text-blue-400" />
                <span className="text-sm text-white/60">Oxirgi tekshiruv</span>
              </div>
              <span className="text-xs text-white/80 font-mono">
                {stats.last_check ? new Date(stats.last_check).toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' }) : '—'}
              </span>
            </div>
            <div className="h-px bg-white/5" />
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <TrendingUp size={14} className="text-violet-400" />
                <span className="text-sm text-white/60">Kunlik o'rtacha</span>
              </div>
              <span className="text-xs text-white/80 font-mono">
                {stats.uptime_days ? Math.round(total / stats.uptime_days) : total} xat/kun
              </span>
            </div>
          </div>

          {/* Haftalik grafik */}
          <div className="glass-card p-4">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp size={14} className="text-white/40" />
              <span className="text-xs text-white/40 uppercase font-semibold">Haftalik faollik</span>
            </div>
            {(() => {
              const weekData = getWeeklyData();
              const maxVal = Math.max(...weekData.map(d => d.sent), 1);
              return (
                <div className="flex items-end justify-between gap-2 h-[100px]">
                  {weekData.map((day, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-[9px] text-white/50 font-mono">{day.sent || ''}</span>
                      <div className="w-full relative flex-1 flex items-end">
                        <div
                          className="w-full rounded-t-md bg-gradient-to-t from-blue-500 to-cyan-400 transition-all duration-500"
                          style={{ height: `${Math.max((day.sent / maxVal) * 100, 4)}%`, minHeight: '3px' }}
                        />
                      </div>
                      <span className="text-[9px] text-white/30 font-medium">{day.label}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Archive Tab */}
      {tab === 'archive' && (
        <div className="space-y-3 animate-fade-in-up">
          {loading ? (
            <div className="flex justify-center py-10">
              <div className="w-8 h-8 border-2 border-white/10 border-t-blue-500 rounded-full animate-spin" />
            </div>
          ) : sentFiles.length === 0 ? (
            <div className="glass-card p-8 text-center">
              <Archive size={24} className="text-white/20 mx-auto mb-2" />
              <p className="text-sm text-white/40">Yuborilgan fayllar hali yo'q</p>
            </div>
          ) : (
            sentFiles.map((item) => (
              <div key={item.id} className="glass-card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                      <FileText size={16} className="text-blue-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-white/80 font-medium truncate">
                        {item.file_names?.join(', ') || 'Fayl'}
                      </p>
                      <p className="text-[10px] text-white/40 mt-0.5 font-mono">{item.recipient_email}</p>
                      <p className="text-[10px] text-white/30 mt-0.5">
                        {item.sender_name || 'Noma\'lum'} • {item.source === 'web' ? 'Web' : 'Telegram'}
                      </p>
                    </div>
                  </div>
                  <span className="text-[9px] text-white/30 flex-shrink-0 whitespace-nowrap">{item.sent_at?.slice(5, 16)}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Block Tab */}
      {tab === 'block' && (
        <div className="space-y-4 animate-fade-in-up">
          {/* Blocked list */}
          {blockedUsers.length > 0 && (
            <div className="glass-card p-4">
              <h3 className="text-xs text-white/40 uppercase font-semibold mb-3">Bloklangan ({blockedUsers.length})</h3>
              <div className="divide-y divide-white/5">
                {blockedUsers.map(u => (
                  <div key={u.chat_id} className="py-2.5 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-white/70">{u.first_name} {u.last_name} {u.username && <span className="text-xs text-blue-400">@{u.username}</span>}</p>
                      <p className="text-[10px] text-white/30 font-mono">ID: {u.chat_id}</p>
                    </div>
                    <button
                      onClick={() => handleUnblock(u.chat_id)}
                      className="px-2.5 py-1.5 rounded-lg bg-emerald-500/10 text-[10px] font-semibold text-emerald-400 hover:bg-emerald-500/20 transition-all active:scale-95"
                    >
                      <ShieldOff size={12} className="inline mr-1" />Ochish
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* All users — block button */}
          <div className="glass-card p-4">
            <h3 className="text-xs text-white/40 uppercase font-semibold mb-3">Foydalanuvchilar ({allUsers.length})</h3>
            {loading ? (
              <div className="flex justify-center py-6">
                <div className="w-6 h-6 border-2 border-white/10 border-t-blue-500 rounded-full animate-spin" />
              </div>
            ) : allUsers.length === 0 ? (
              <p className="text-sm text-white/40 text-center py-4">Foydalanuvchilar yo'q</p>
            ) : (
              <div className="divide-y divide-white/5 max-h-[300px] overflow-y-auto">
                {allUsers.map(user => {
                  const isBlocked = blockedUsers.some(b => b.chat_id === user.id);
                  return (
                    <div key={user.id} className="py-2.5 flex items-center justify-between">
                      <div>
                        <p className="text-sm text-white/70">
                          {user.first_name} {user.last_name} {user.username && <span className="text-xs text-blue-400">@{user.username}</span>}
                        </p>
                        <p className="text-[10px] text-white/30 font-mono">ID: {user.id}</p>
                      </div>
                      {isBlocked ? (
                        <span className="text-[10px] text-red-400 px-2 py-1 bg-red-500/10 rounded-lg">Bloklangan</span>
                      ) : (
                        <button
                          onClick={() => handleBlock(user.id)}
                          className="px-2.5 py-1.5 rounded-lg bg-red-500/10 text-[10px] font-semibold text-red-400 hover:bg-red-500/20 transition-all active:scale-95"
                        >
                          <Shield size={12} className="inline mr-1" />Bloklash
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Logs Tab */}
      {tab === 'logs' && (
        <div className="space-y-3 animate-fade-in-up">
          {/* Controls */}
          <div className="glass-card p-3 flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[120px]">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                type="text"
                value={logSearch}
                onChange={(e) => setLogSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchLogs()}
                placeholder="Qidirish..."
                className="w-full pl-7 py-1.5 text-[10px]"
              />
            </div>
            <select
              value={logLevel}
              onChange={(e) => { setLogLevel(e.target.value); }}
              className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-[10px] text-white/70 outline-none"
            >
              <option value="">Hammasi</option>
              <option value="ERROR">ERROR</option>
              <option value="WARNING">WARNING</option>
              <option value="INFO">INFO</option>
              <option value="DEBUG">DEBUG</option>
            </select>
            <select
              value={logFile}
              onChange={(e) => { setLogFile(e.target.value); }}
              className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-[10px] text-white/70 outline-none"
            >
              <option value="app">app.log</option>
              <option value="error">error.log</option>
            </select>
            <button
              onClick={fetchLogs}
              className="w-7 h-7 rounded-lg bg-blue-500/10 flex items-center justify-center hover:bg-blue-500/20 transition-colors"
            >
              <RefreshCw size={12} className={logsLoading ? 'text-blue-400 animate-spin' : 'text-blue-400'} />
            </button>
          </div>

          {/* Log output */}
          <div className="glass-card p-3 max-h-[400px] overflow-y-auto">
            {logsLoading ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 border-2 border-white/10 border-t-blue-500 rounded-full animate-spin" />
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-8">
                <Terminal size={24} className="text-white/20 mx-auto mb-2" />
                <p className="text-xs text-white/40">Loglar topilmadi</p>
              </div>
            ) : (
              <div className="font-mono text-[10px] leading-relaxed space-y-0.5">
                {logs.map((entry, i) => (
                  <div
                    key={i}
                    className={'py-0.5 px-1 rounded ' + (
                      entry.level === 'ERROR' ? 'bg-red-500/10 text-red-300' :
                      entry.level === 'WARNING' ? 'bg-amber-500/10 text-amber-300' :
                      entry.level === 'DEBUG' ? 'text-white/30' :
                      'text-white/60'
                    )}
                  >
                    {entry.raw}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
